import { Grid } from '@mui/material';
import { find, max } from 'lodash';
import { Field } from 'react-final-form';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ActionButton from '../../../../../components/ActionButton';
import { getDisplayedExtraPrice } from '../../../helpers/shipment';
import { getFieldsToUpdate } from '../helpers';
import { Plane, Road } from '../../../../../components/icons';
import { FormHelpers, NetworkHelpers } from '../../../../../helpers';
import { SHIPMENT } from '../../../../../constants';
import { FREE } from '../../../../../constants/strings';
import configSlice from '../../../../../redux/configSlice';

function getServiceIcon(network, serviceIconsDisabled) {
  if (serviceIconsDisabled) {
    return null;
  }

  const isPlane =
    NetworkHelpers.removeBusinessUnit(network.service?.serviceKey) === '0';

  if (isPlane) {
    return <Plane styles={{ width: '32px', height: '32px' }} />;
  }

  return <Road styles={{ width: '32px', height: '32px' }} />;
}

function isShipToShop(networkKey) {
  return networkKey === SHIPMENT.NETWORK_KEYS.NETWORK_KEY_SHIP_TO_SHOP;
}

function getActionButtonTitle(networkDetails, network, arriveContent) {
  if (networkDetails?.title) {
    const title = isShipToShop(network.networkKey)
      ? arriveContent?.title || networkDetails.title()
      : networkDetails.title(network?.service?.serviceDesc);

    return `${title}${isShipToShop(network.networkKey) ? ':' : ''}`;
  }

  return network.networkDesc;
}

function getActionButtonPrice(networkKey, extraPrice) {
  if (!isShipToShop(networkKey)) {
    return getDisplayedExtraPrice(extraPrice);
  }

  return FREE;
}

function getNetworkDesc(network, shipmentDate, collectFrom, arriveContent) {
  if (isShipToShop(network.networkKey)) {
    return (
      arriveContent?.body ||
      SHIPMENT.NETWORK_DETAILS[network.networkKey]?.desc({ collectFrom })
    );
  }

  return SHIPMENT.NETWORK_DETAILS[network.networkKey]?.desc({
    shipmentDate,
    collectFrom,
    network,
  });
}

function getOptionDescription(
  network,
  shipmentDate,
  collectFrom,
  extraPrice,
  arriveContent
) {
  const shipToShop = isShipToShop(network.networkKey);
  let desc = getNetworkDesc(network, shipmentDate, collectFrom, arriveContent);

  if (!shipToShop && extraPrice) {
    desc += `, Extra price: ${getDisplayedExtraPrice(extraPrice)}`;
  } else if (shipToShop) {
    desc += `, Free`;
  }

  return desc;
}

const ServiceButtonsView = ({
  networks,
  price,
  shipmentDate,
  fieldName,
  form,
  collectFrom,
  formValues,
  disabled,
  serviceIconsDisabled,
  arriveContent,
}) => (
  <Field name={fieldName}>
    {({ input }) => {
      if (networks.findIndex(n => n.networkKey === input.value) === -1) {
        form.change(fieldName, undefined);
      }

      // Generate unique IDs for aria attributes
      const groupLabelId = `${fieldName}-label`;
      const groupDescId = `${fieldName}-desc`;

      return (
        <>
          {/* Visually hidden label and description for accessibility */}
          <span
            id={groupLabelId}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
            }}
          >
            Select a delivery service
          </span>
          <span
            id={groupDescId}
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
            }}
          >
            Choose one of the available delivery networks below.
          </span>
          <Grid
            container
            spacing={2}
            sx={{ my: 2, alignItems: 'stretch' }}
            aria-labelledby={groupLabelId}
            aria-describedby={groupDescId}
            role='radiogroup'
          >
            {networks.map(network => {
              const { extraPrice } =
                find(price.arrivalServicesExtraPrices, [
                  'networkKey',
                  network.networkKey,
                ]) || {};
              const networkDetails =
                SHIPMENT.NETWORK_DETAILS[network.networkKey];

              // For aria-describedby, provide extra info for screen readers
              const optionDescId =
                `${fieldName}-option-${network.networkDesc}-desc`.replaceAll(
                  /[. ]/g,
                  '-'
                );
              const radioDescId =
                `${fieldName}-radio-${network.networkDesc}-desc`.replaceAll(
                  /[. ]/g,
                  '-'
                );
              const labelDescId =
                `${fieldName}-radio-${network.networkDesc}-label`.replaceAll(
                  /[. ]/g,
                  '-'
                );

              return (
                <Grid
                  container
                  key={network.networkKey}
                  size={{
                    md: max([12 / networks.length, 6]),
                    xs: 12,
                  }}
                  component='label'
                  htmlFor={radioDescId}
                  sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  <ActionButton
                    id={radioDescId}
                    selected={input.value === network.networkKey}
                    title={getActionButtonTitle(
                      networkDetails,
                      network,
                      arriveContent
                    )}
                    bodyText={getNetworkDesc(
                      network,
                      shipmentDate,
                      collectFrom,
                      arriveContent
                    )}
                    price={getActionButtonPrice(network.networkKey, extraPrice)}
                    cardOnclick={() => {
                      input.onChange(network.networkKey);
                      FormHelpers.bulkUpdate(
                        form,
                        getFieldsToUpdate({
                          network,
                          values: formValues,
                        })
                      );
                    }}
                    AdditionalIconComponent={() =>
                      getServiceIcon(network, serviceIconsDisabled)
                    }
                    disabled={disabled}
                  />
                  {/* Option description for screen readers */}
                  <span
                    id={optionDescId}
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      overflow: 'hidden',
                      clip: 'rect(0 0 0 0)',
                    }}
                  >
                    {getOptionDescription(
                      network,
                      shipmentDate,
                      collectFrom,
                      extraPrice,
                      arriveContent
                    )}
                  </span>
                  <span
                    id={labelDescId}
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      overflow: 'hidden',
                      clip: 'rect(0 0 0 0)',
                    }}
                  >
                    {isShipToShop(network.networkKey)
                      ? arriveContent?.title || networkDetails?.title()
                      : networkDetails?.title(network?.service?.serviceDesc) ||
                        network.networkDesc}
                  </span>
                </Grid>
              );
            })}
          </Grid>
        </>
      );
    }}
  </Field>
);

export const ServiceButtons = props => {
  const { sendParcel } = useSelector(configSlice.selectors.getFeatureConfig);
  const arriveContent = sendParcel?.dropOff?.arriveContent;

  return <ServiceButtonsView {...props} arriveContent={arriveContent} />;
};

PropTypes.ServiceButtons = {
  networks: PropTypes.array.isRequired,
  price: PropTypes.object.isRequired,
  shipmentDate: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  formValues: PropTypes.object.isRequired,
  collectFrom: PropTypes.string,
  disabled: PropTypes.bool,
  serviceIconsDisabled: PropTypes.bool,
};
